import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { ConfigLoadingStatus, IDigitalMenuConfigData } from "../types";

interface IDigitalMenuConfigContext {
    config: IDigitalMenuConfigData | null;
    loadingStatus: ConfigLoadingStatus;
    error: string | null;
    setConfig: (config: IDigitalMenuConfigData) => void;
    setLoadingStatus: (status: ConfigLoadingStatus) => void;
    setError: (error: string | null) => void;
    isConfigLoaded: boolean;
}

interface IDigitalMenuConfigProviderProps {
    children: ReactNode;
}

const DigitalMenuConfigContext = createContext<IDigitalMenuConfigContext | undefined>(undefined);

export const DigitalMenuConfigProvider: React.FC<IDigitalMenuConfigProviderProps> = ({
    children,
}) => {
    const [config, setConfigState] = useState<IDigitalMenuConfigData | null>(null);
    const [loadingStatus, setLoadingStatusState] = useState<ConfigLoadingStatus>(
        ConfigLoadingStatus.IDLE
    );
    const [error, setErrorState] = useState<string | null>(null);

    const setConfig = useCallback((newConfig: IDigitalMenuConfigData) => {
        setConfigState(newConfig);
        setLoadingStatusState(ConfigLoadingStatus.SUCCESS);
        setErrorState(null);
    }, []);

    const setLoadingStatus = useCallback((status: ConfigLoadingStatus) => {
        setLoadingStatusState(status);
    }, []);

    const setError = useCallback((errorMessage: string | null) => {
        setErrorState(errorMessage);
        if (errorMessage) {
            setLoadingStatusState(ConfigLoadingStatus.ERROR);
        }
    }, []);

    const isConfigLoaded = useMemo(
        () => loadingStatus === ConfigLoadingStatus.SUCCESS && config !== null,
        [loadingStatus, config]
    );

    const contextValue: IDigitalMenuConfigContext = useMemo(
        () => ({
            config,
            loadingStatus,
            error,
            setConfig,
            setLoadingStatus,
            setError,
            isConfigLoaded,
        }),
        [config, loadingStatus, error, setConfig, setLoadingStatus, setError, isConfigLoaded]
    );

    return (
        <DigitalMenuConfigContext.Provider value={contextValue}>
            {children}
        </DigitalMenuConfigContext.Provider>
    );
};

// Default context value for SSR and when provider is not available
const DEFAULT_CONTEXT_VALUE: IDigitalMenuConfigContext = {
    config: null,
    loadingStatus: ConfigLoadingStatus.IDLE,
    error: null,
    setConfig: () => {},
    setLoadingStatus: () => {},
    setError: () => {},
    isConfigLoaded: false,
};

export const useDigitalMenuConfig = (): IDigitalMenuConfigContext => {
    const context = useContext(DigitalMenuConfigContext);

    // Return default values during SSR or when provider is not available
    if (isEmpty(context)) {
        return DEFAULT_CONTEXT_VALUE;
    }

    return context;
};
